import { useMemo, useState } from "react";
import { FileText, Lock, MessageSquareText, Plus, Search, Trash2 } from "lucide-react";
import { Link } from "react-router";
import type { KbPage, KbWiki, SeedSchema } from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";
import { seedPageFillStats, seedPageStatusBadgeLabel } from "@/lib/kb-page-status";
import { kbWorkbenchPath, type KbContentScope } from "@/lib/kb-routes";
import { cn } from "@/lib/utils";
import { AdminPill } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function pageMatchesQuery(page: KbPage, query: string): boolean {
  if (!query) return true;
  const title = page.title?.toLowerCase() ?? "";
  const slug = page.slug?.toLowerCase() ?? "";
  return title.includes(query) || slug.includes(query);
}

export type WikiTreeScope = "enterprise" | "brand";

type WikiTreeProps = {
  accountId: string;
  /** Brand content stack — when set, only that brand wiki is listed. */
  brandId?: string | null;
  scope: WikiTreeScope;
  /** Pages for the active wiki (workbench). Omit on inbox-only chrome. */
  pages?: KbPage[];
  /** Active wiki seed_schema — used for per-seed remaining-% badges. */
  seedSchema?: SeedSchema | null;
  selectedPageId?: number | null;
  canWrite?: boolean;
  pagesLoading?: boolean;
  onSelectPage?: (pageId: number) => void;
  onCreateFreePage?: () => void;
  onAiKnowledgeEntry?: () => void;
  onDeletePage?: (pageId: number) => void;
};

export function filterWikisForScope(
  wikis: KbWiki[],
  scope: WikiTreeScope,
  brandId?: string | null,
): KbWiki[] {
  if (scope === "enterprise") {
    return wikis.filter((wiki) => wiki.kind === "enterprise");
  }
  if (!brandId) return [];
  const id = Number(brandId);
  return wikis.filter((wiki) => wiki.kind === "brand" && wiki.brand_id === id);
}

export function WikiTree({
  accountId,
  brandId = null,
  scope,
  pages = [],
  seedSchema = null,
  selectedPageId = null,
  canWrite = false,
  pagesLoading = false,
  onSelectPage,
  onCreateFreePage,
  onAiKnowledgeEntry,
  onDeletePage,
}: WikiTreeProps) {
  const [entryQuery, setEntryQuery] = useState("");
  const contentScope: KbContentScope = { accountId, brandId };
  const workbenchHref = kbWorkbenchPath(contentScope);
  const navLabel = scope === "enterprise" ? "企业知识库导航" : "品牌知识库导航";

  const queryNormalized = entryQuery.trim().toLowerCase();
  const seedPages = useMemo(
    () => pages.filter((page) => page.is_seed && pageMatchesQuery(page, queryNormalized)),
    [pages, queryNormalized],
  );
  const freePages = useMemo(
    () => pages.filter((page) => !page.is_seed && pageMatchesQuery(page, queryNormalized)),
    [pages, queryNormalized],
  );
  const hasQuery = queryNormalized.length > 0;
  const filterEmpty =
    hasQuery && seedPages.length === 0 && freePages.length === 0 && !pagesLoading;

  const showPageLists = Boolean(onSelectPage) || pages.length > 0 || pagesLoading;
  const showNewEntry = canWrite && (onCreateFreePage || onAiKnowledgeEntry);
  const showToolbar = showPageLists || showNewEntry;

  return (
    <nav className="flex h-full flex-col gap-3 overflow-hidden text-sm" aria-label={navLabel}>
      {showToolbar ? (
        <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-2">
          {showPageLists ? (
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={entryQuery}
                onChange={(e) => setEntryQuery(e.target.value)}
                placeholder={KB_COPY.searchEntriesPlaceholder}
                className="h-8 rounded-md pl-7 text-sm"
                aria-label={KB_COPY.searchEntriesPlaceholder}
              />
            </div>
          ) : null}
          {showNewEntry ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="size-8 shrink-0"
                  aria-label={KB_COPY.newEntryMenu}
                  title={KB_COPY.newEntryMenu}
                >
                  <Plus className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-auto min-w-0 rounded-xl p-1">
                {onCreateFreePage ? (
                  <DropdownMenuItem
                    className="cursor-pointer gap-2 rounded-lg"
                    onSelect={() => onCreateFreePage()}
                  >
                    <FileText className="size-3.5" />
                    {KB_COPY.newFreeEntry}
                  </DropdownMenuItem>
                ) : null}
                {onAiKnowledgeEntry ? (
                  <DropdownMenuItem
                    className="cursor-pointer gap-2 rounded-lg"
                    onSelect={() => onAiKnowledgeEntry()}
                  >
                    <MessageSquareText className="size-3.5" />
                    {KB_COPY.aiKnowledgeEntry}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
        {showPageLists ? (
          pagesLoading ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">正在加载知识条目…</p>
          ) : filterEmpty ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">{KB_COPY.searchEntriesEmpty}</p>
          ) : (
            <>
              {!hasQuery || seedPages.length > 0 ? (
                <section className="p-1">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {KB_COPY.seedSection}
                  </div>
                  {seedPages.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-muted-foreground">{KB_COPY.emptySeeds}</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {seedPages.map((page) => (
                        <PageRow
                          key={page.id}
                          page={page}
                          seedSchema={seedSchema}
                          selected={selectedPageId === page.id}
                          canWrite={canWrite}
                          onSelect={() => onSelectPage?.(page.id)}
                          onDelete={onDeletePage ? () => onDeletePage(page.id) : undefined}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              ) : null}

              {!hasQuery || freePages.length > 0 ? (
                <section className="p-1">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {KB_COPY.freeSection}
                  </div>
                  {freePages.length === 0 ? (
                    <p className="px-2 py-3 text-xs text-muted-foreground">{KB_COPY.emptyFree}</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {freePages.map((page) => (
                        <PageRow
                          key={page.id}
                          page={page}
                          selected={selectedPageId === page.id}
                          canWrite={canWrite}
                          onSelect={() => onSelectPage?.(page.id)}
                          onDelete={onDeletePage ? () => onDeletePage(page.id) : undefined}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              ) : null}
            </>
          )
        ) : (
          <div className="px-2 py-2">
            <Link
              to={workbenchHref}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {KB_COPY.backToWorkbench}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}

function PageRow({
  page,
  seedSchema = null,
  selected,
  canWrite,
  onSelect,
  onDelete,
}: {
  page: KbPage;
  seedSchema?: SeedSchema | null;
  selected: boolean;
  canWrite: boolean;
  onSelect: () => void;
  onDelete?: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const seedDef = page.is_seed
    ? seedSchema?.pages?.find((def) => def.slug === page.slug)
    : undefined;
  const seedStats = page.is_seed ? seedPageFillStats(page, seedDef) : null;
  const seedBadge = seedStats ? seedPageStatusBadgeLabel(seedStats) : null;

  return (
    <li>
      <div className={cn("group flex items-center gap-1 rounded-md pr-1", selected && "bg-accent")}>
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent/60"
        >
          {page.is_seed ? (
            <Lock className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <FileText className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{page.title}</span>
          {seedBadge && seedStats ? (
            <AdminPill tone={seedStats.complete ? "success" : "warning"}>{seedBadge}</AdminPill>
          ) : null}
        </button>
        {canWrite && !page.is_seed && onDelete ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 opacity-0 group-hover:opacity-100"
              onClick={(event) => {
                event.stopPropagation();
                setDeleteOpen(true);
              }}
              aria-label={`删除 ${page.title}`}
            >
              <Trash2 className="size-3.5" />
            </Button>
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>删除「{page.title}」？</DialogTitle>
                  <DialogDescription>{KB_COPY.deleteConfirm}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>
                    取消
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setDeleteOpen(false);
                      onDelete();
                    }}
                  >
                    删除
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      </div>
    </li>
  );
}
